import type { InputHTMLAttributes } from 'react';
import { forwardRef, useCallback } from 'react';

import type { ColorName } from 'styles/config/colors';

import type { CompoundType } from '@polyai/common/types/helpers';

import { cn } from 'utils/cn';
import { colorCssVar } from 'utils/colorCssVar';

type HTMLPropTypes = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onKeyPress' | 'onKeyPressCapture' | 'onKeyDownCapture' | 'onKeyUpCapture'
>;

type PropTypes = CompoundType<
  HTMLPropTypes & {
    isKeyboardSafe?: boolean;
    textColor?: ColorName;
    placeholderColor?: ColorName;
  }
>;

/**
 * This component stops propagation of keyboard events to the document
 * so that our keyboard shortcut listeners are not triggered.
 *
 * It should be used instead of <input/> tags unless absolutely
 * necessary.
 */
export const KeyboardSafeInput = forwardRef<HTMLInputElement, PropTypes>(
  ({ textColor, placeholderColor, ...props }, ref) => {
    const wrapKeyboardFn = useCallback(
      (fnName: keyof HTMLPropTypes) =>
        (...args: any[]) => {
          props[fnName]?.(...args);
          if (!props.isKeyboardSafe) {
            args[0]?.stopPropagation?.();
          }
        },
      // biome-ignore lint/correctness/useExhaustiveDependencies: existing
      [props],
    );

    const { isKeyboardSafe: _, ...restProps } = props;

    return (
      <input
        {...restProps}
        ref={ref}
        className={cn(
          placeholderColor && 'placeholder:[color:var(--placeholder-color)]',
          restProps.className,
        )}
        role="input"
        style={{
          ...(textColor && { color: colorCssVar(textColor) }),
          ...(placeholderColor && {
            ['--placeholder-color' as string]: colorCssVar(placeholderColor),
          }),
          ...restProps.style,
        }}
        onKeyDown={wrapKeyboardFn('onKeyDown')}
        onKeyUp={wrapKeyboardFn('onKeyUp')}
      />
    );
  },
);
